import React from 'react';
import { Table, ConfigProvider, Empty, Space, Button, Typography } from 'antd';
import { CalendarOutlined, CloudDownloadOutlined, FilePdfOutlined, InfoCircleOutlined } from '@ant-design/icons';

import { CustomTooltip } from '~components/Tooltip';
import { dates } from '~library/dates';
import InvoiceStatus from './InvoiceStatus';
import { useIsMobileView } from '../../../hooks/useIsMobileView';
import { formatCentsToDollar } from '../../../assets/currencies';
import { createUsNumberFormat } from '../../../utils/createUsNumberFormat';

function BillingHistoryTable({ isLoading = false, items = [], handleOpenInvoicePdf, hideRendersColumns = false }) {
  const isMobile = useIsMobileView();

  const visibleColumns = React.useMemo(() => {
    const columns = [
      {
        title: 'Invoice',
        width: '99%',
        render: (_text, item) => (
          <Space>
            {!isMobile && (item.status === 'payment_due' ? <CalendarOutlined /> : <FilePdfOutlined />)}
            <Typography.Text>#{item.id}</Typography.Text>
          </Space>
        ),
      },
      {
        title: 'Issue date',
        className: 'col-min',
        render: (_text, item) => dates.calendarDay(item.date),
      },
      {
        title: 'Due date',
        className: 'col-min',
        render: (_text, item) => dates.calendarDay(item.dueDate),
        isHidden: isMobile,
      },
      {
        title: 'Amount',
        dataIndex: 'totalInCent',
        render: (text) => formatCentsToDollar(text),
      },
      {
        title: (
          <Space size={4}>
            <span>Charged Amount</span>
            <CustomTooltip
              placement="top"
              message="For plan changes, this can be a prorated charge for the current billing period."
            >
              <InfoCircleOutlined />
            </CustomTooltip>
          </Space>
        ),
        className: 'col-min',
        dataIndex: 'amountPaidInCent',
        render: (text) => (typeof text === 'number' ? formatCentsToDollar(text) : '-'),
      },
      {
        title: 'Included renders',
        className: 'col-min',
        dataIndex: 'includedRenders',
        render: (text) => createUsNumberFormat(text || 0),
        isHidden: hideRendersColumns,
      },
      {
        title: 'Overage billed as',
        className: 'col-min',
        dataIndex: 'overageBilledAsPlanName',
        render: (text) => text || '-',
        isHidden: hideRendersColumns,
      },
      {
        title: 'Extra renders',
        className: 'col-min',
        dataIndex: 'extraRenders',
        render: (text) => createUsNumberFormat(text || 0),
        isHidden: hideRendersColumns,
      },
      {
        title: 'Total renders',
        className: 'col-min',
        dataIndex: 'renders',
        render: (text) => createUsNumberFormat(text || 0),
        isHidden: hideRendersColumns,
      },
      {
        title: 'Payment date',
        className: 'col-min',
        render: (_text, item) => dates.dayOfPointInTime(item.paidAt),
        isHidden: isMobile,
      },
      {
        title: 'Status',
        render: (_text, item) => (
          <Space>
            <InvoiceStatus invoice={item} />
            <Button icon={<CloudDownloadOutlined />} onClick={() => handleOpenInvoicePdf(item.id)}></Button>
          </Space>
        ),
      },
    ];

    return columns.filter((column) => !column.isHidden);
  }, [isMobile, handleOpenInvoicePdf, hideRendersColumns]);

  return (
    <ConfigProvider
      renderEmpty={() => <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="You don't have any invoices yet." />}
    >
      <Table
        bordered={!isLoading}
        rowKey="id"
        loading={isLoading}
        columns={visibleColumns}
        dataSource={items}
        pagination={false}
        showHeader={!!items.length}
        scroll={{ x: true }}
      />
    </ConfigProvider>
  );
}

export default BillingHistoryTable;
